import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Heart, MessageCircle, Share2, Repeat2, AudioLines,
  Play, Volume2, VolumeX, UserPlus, Check, Music2, Pencil, Disc3, Droplet,
} from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl, formatCount } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "./Avatar";

const RailButton = ({ onClick, active, activeColor = "#FFD60A", icon: IconC, label, testid, filled }) => (
  <button onClick={onClick} data-testid={testid} className="flex flex-col items-center gap-1 group">
    <span
      className="h-12 w-12 rounded-full bg-black/40 backdrop-blur-md border border-white/15 flex items-center justify-center transition-transform group-hover:scale-110 group-active:scale-95"
      style={active ? { background: activeColor, borderColor: activeColor } : {}}
    >
      <IconC className={`h-6 w-6 ${active ? "text-black" : "text-white"}`} strokeWidth={1.8} fill={filled && active ? "black" : "none"} />
    </span>
    <span className="text-xs font-semibold text-white drop-shadow">{label}</span>
  </button>
);

export const ReelItem = ({ video, isActive, shouldLoad = true, muted, onToggleMute, onOpenComments, onOpenShare, onShareIntent, onOpenManage, onUpdate, onPlayed }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [paused, setPaused] = useState(false);

  const isArtist = user && (user.is_artist || user.role === "admin");
  const isManager = user && (user.role === "admin" || user.id === video.artist_id);
  const isAdminOwner = user && user.role === "admin";
  const canShare = user && (isAdminOwner || user.id === video.artist_id);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (isActive) {
      el.currentTime = el.currentTime || 0;
      el.volume = 1.0;
      const p = el.play();
      if (p) p.then(() => setPaused(false)).catch(() => {});
      onPlayed && onPlayed(video.id);
    } else {
      el.pause();
    }
    // eslint-disable-next-line
  }, [isActive]);

  const togglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) { el.play(); setPaused(false); } else { el.pause(); setPaused(true); }
  };

  const requireLogin = () => { toast.error("Log in to continue"); navigate("/login"); };

  const like = async (e) => {
    e.stopPropagation();
    if (!user) return requireLogin();
    try {
      const { data } = await api.post(`/videos/${video.id}/like`);
      onUpdate(video.id, { liked: data.liked, likes: data.likes });
    } catch (err) { toast.error(err.response?.data?.detail || "Could not like"); }
  };

  const repost = async (e) => {
    e.stopPropagation();
    if (!user) return requireLogin();
    if (isArtist) return toast.error("Reposting is for the public");
    try {
      const { data } = await api.post(`/videos/${video.id}/repost`);
      onUpdate(video.id, { reposted: data.reposted, reposts: data.reposts });
      toast.success(data.reposted ? "Reposted" : "Repost removed");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not repost"); }
  };

  const follow = async (e) => {
    e.stopPropagation();
    if (!user) return requireLogin();
    if (user.id === video.artist_id) return;
    try {
      const { data } = await api.post(`/artists/${video.artist_id}/follow`);
      onUpdate(video.id, { following: data.following });
    } catch (err) { toast.error("Could not follow"); }
  };

  return (
    <div className="relative h-full w-full overflow-hidden bg-black snap-start flex items-center justify-center">
      {/* Blurred backdrop */}
      {video.thumbnail && <img src={mediaUrl(video.thumbnail)} alt="" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-40" />}
      <div className="absolute inset-0 bg-black/30" />

      {/* Portrait video (full vertical width & height) */}
      <div className="relative h-full w-full mx-auto" onClick={togglePlay}>
        {shouldLoad ? (
          <video
            ref={videoRef}
            data-testid={`reel-video-${video.id}`}
            src={mediaUrl(video.video_url)}
            poster={video.thumbnail ? mediaUrl(video.thumbnail) : undefined}
            loop
            muted={muted}
            playsInline
            preload="auto"
            className="w-full h-full object-cover"
          />
        ) : (
          video.thumbnail
            ? <img src={mediaUrl(video.thumbnail)} alt={video.title} className="w-full h-full object-cover" />
            : <div className="w-full h-full bg-zinc-900" />
        )}
        {paused && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="h-20 w-20 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center">
              <Play className="h-9 w-9 text-white fill-white ml-1" />
            </span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30 pointer-events-none" />
      </div>

      {/* Top bar */}
      <div className="absolute top-4 left-0 right-0 px-4 flex items-center justify-between">
        <span className="px-3 py-1 bg-black/50 backdrop-blur-sm text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-100 flex items-center gap-1.5">
          <Music2 className="h-3 w-3 text-[#FFD60A]" /> {video.genre}
        </span>
        <button onClick={(e) => { e.stopPropagation(); onToggleMute(); }} data-testid="mute-toggle" className="h-10 w-10 rounded-full bg-black/40 backdrop-blur-md border border-white/15 flex items-center justify-center text-white">
          {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
        </button>
      </div>

      {/* Owner/admin manage button */}
      {isManager && (
        <button
          onClick={(e) => { e.stopPropagation(); onOpenManage(video); }}
          data-testid={`manage-${video.id}`}
          className="absolute top-16 right-4 z-20 flex items-center gap-1.5 px-3 py-1.5 bg-[#FFD60A] text-black text-xs font-semibold hover:bg-[#FFD60A]/90 transition-colors"
        >
          <Pencil className="h-3.5 w-3.5" /> Edit
        </button>
      )}


      {/* Bottom-left info */}
      <div className="absolute bottom-6 left-4 right-24 z-10">
        <span className="inline-flex items-center gap-1.5 mb-2">
          <Disc3 className="h-4 w-4 text-[#FFD60A]" strokeWidth={1.5} />
          <span className="font-brand text-sm text-[#FFD60A] leading-none">NEXTtt</span>
        </span>
        <h2 className="font-display text-xl font-bold text-white leading-tight drop-shadow">
          <span className="text-[#FFD60A]">Title:</span> {video.title}
        </h2>
        <Link to={`/artist/${video.artist_id}`} onClick={(e) => e.stopPropagation()} className="inline-flex flex-col mt-1.5 leading-tight">
          <span className="font-display text-lg font-bold text-white">{video.artist_name}</span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#FFD60A]">Music Artist</span>
        </Link>
        {video.description && <p className="text-sm text-zinc-300 mt-1 line-clamp-2 drop-shadow">{video.description}</p>}
        <div className="mt-2 flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-200 bg-black/40 backdrop-blur-sm px-2.5 py-1 rounded-full">
            <AudioLines className="h-3.5 w-3.5 text-[#FFD60A]" /> {formatCount(video.streams)} streams
          </span>
          <span data-testid={`drops-${video.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#FFD60A] bg-black/40 backdrop-blur-sm px-2.5 py-1 rounded-full">
            <Droplet className="h-3.5 w-3.5 text-[#FFD60A]" fill="#FFD60A" /> {formatCount(video.streams)} drops
          </span>
        </div>
      </div>

      {/* Right action rail */}
      <div className="absolute bottom-6 right-3 z-10 flex flex-col items-center gap-5">
        <Link to={`/artist/${video.artist_id}`} onClick={(e) => e.stopPropagation()} className="relative">
          <Avatar src={video.artist_avatar} name={video.artist_name} className="h-12 w-12 rounded-full border-2 border-white" textClass="text-lg" />
          {user && user.id !== video.artist_id && (
            <button onClick={follow} data-testid={`follow-${video.artist_id}`} className="absolute -bottom-2 left-1/2 -translate-x-1/2 h-5 w-5 rounded-full bg-[#FFD60A] flex items-center justify-center">
              {video.following ? <Check className="h-3 w-3 text-white" /> : <UserPlus className="h-3 w-3 text-white" />}
            </button>
          )}
        </Link>

        <RailButton testid={`like-${video.id}`} onClick={like} active={video.liked} filled icon={Heart} label={formatCount(video.likes)} />
        <RailButton testid={`comment-${video.id}`} onClick={(e) => { e.stopPropagation(); onOpenComments(video); }} icon={MessageCircle} label={formatCount(video.comment_count)} />
        {canShare && (
          <button onClick={(e) => { e.stopPropagation(); onShareIntent(video); }} data-testid={`share-${video.id}`} className="flex flex-col items-center gap-1 group">
            <span className="h-12 w-12 rounded-full bg-[#FFD60A] border border-[#FFD60A] flex items-center justify-center transition-transform group-hover:scale-110 relative">
              <Share2 className="h-6 w-6 text-black" strokeWidth={1.8} />
              <span className="absolute -top-1 -right-1 bg-black text-[#FFD60A] text-[8px] font-bold px-1 rounded-full border border-[#FFD60A]">{isAdminOwner ? "FREE" : "$1"}</span>
            </span>
            <span className="text-xs font-semibold text-white drop-shadow">{formatCount(video.shares)}</span>
          </button>
        )}
      </div>
    </div>
  );
};
